"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { ProductCategoriesDocument, ProductCategoriesQuery } from "@/gql/graphql";
import { defaultLocale } from "@/config";
import { executeGraphQL } from "@/lib/graphql";
import { handleGraphqlLocale } from "@/lib/utils/util";

interface CategoryContextType {
	categories: ProductCategoriesQuery["categories"] | undefined;
	setCategories: React.Dispatch<React.SetStateAction<ProductCategoriesQuery["categories"] | undefined>>;
}

const getSortValue = (node: any) => {
	const metadata = node.metadata?.find((item: any) => item.key === "sortNumber");
	return metadata ? parseInt(metadata.value) || 1 : 999;
};

// 排序 categories 及其直接子集（两层）
const sortCategories = (
	categories: ProductCategoriesQuery["categories"],
): ProductCategoriesQuery["categories"] => {
	if (!categories?.edges) {
		return categories;
	}

	// 对第一层的 edges 进行排序
	const sortedEdges = [...categories.edges].sort((a, b) => {
		const sortValueA = getSortValue(a.node);
		const sortValueB = getSortValue(b.node);
		return sortValueA - sortValueB;
	});

	// 处理每个节点的直接子集（第二层）
	const processedEdges = sortedEdges.map((edge) => {
		if (edge.node.children?.edges) {
			// 对子节点进行排序
			const sortedChildrenEdges = [...edge.node.children.edges].sort((a, b) => {
				const sortValueA = getSortValue(a.node);
				const sortValueB = getSortValue(b.node);
				return sortValueA - sortValueB;
			});

			return {
				...edge,
				node: {
					...edge.node,
					children: {
						...edge.node.children,
						edges: sortedChildrenEdges,
					},
				},
			};
		}
		return edge;
	});

	return {
		...categories,
		edges: processedEdges,
	};
};

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export const useCategory = () => {
	const context = useContext(CategoryContext);
	if (!context) {
		throw new Error("useCategory must be used within a CategoryProvider");
	}
	return context;
};

export const CategoryProvider: React.FC<{
	children: React.ReactNode;
	initialCategories?: ProductCategoriesQuery["categories"];
	locale: string;
}> = ({ children, initialCategories, locale }) => {
	const [categories, setCategories] = useState<ProductCategoriesQuery["categories"]>(initialCategories);

	useEffect(() => {
		const fetchCategory = async () => {
			const result = await executeGraphQL(ProductCategoriesDocument, {
				withAuth: false,
				variables: { locale: handleGraphqlLocale(locale || defaultLocale), first: 20 },
			});
			if (result && result.categories) {
				const sortedCategories = sortCategories(result.categories);
				setCategories(sortedCategories);
			}
		};

		fetchCategory();
	}, []);
	return (
		<CategoryContext.Provider value={{ categories, setCategories }}>{children}</CategoryContext.Provider>
	);
};
